import React, { useEffect, useState } from "react";
import DropDown from "sr/helpers/ui-components/DropDown.tsx";
import { Button } from "sr/helpers/ui-components/company/Button";
import { FormattedMessage, useIntl } from "react-intl";
import { getTask } from "sr/utils/api/company/customer/getTask";
import { useFormik } from "formik";
import tasklistSchema from "sr/utils/schemas/company/customer/TasklistSchema";
import { updateTasklist } from "sr/utils/api/company/customer/updateTasklist";
import { useParams } from "react-router-dom";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
} from "@hello-pangea/dnd";
import { reorderTasklist } from "sr/utils/api/company/customer/reorderTasklist";

const DragDropContextComponent = DragDropContext as any;
const DroppableComponent = Droppable as any;
const DraggableComponent = Draggable as any;

export const Task = (props) => {
  const isReadOnly = props?.readOnly === true;
  const [loader, setLoader] = useState(false);
  const [selectType, setselectType] = useState();
  const [task, setTask] = useState("");
  const [description, setDescription] = useState("");
  const [showEdit, setshowEdit] = useState(null);
  const params = useParams();
  const [checklistId, setchecklistId] = useState(null);
  const activeChecklistId = props?.checklistId || checklistId;
  const [tasks, setTasks] = useState<any>([]);
  const [isMediaUploadRequired, setIsMediaUploadRequired] = useState(false);
  const [updatingMediaTaskIds, setUpdatingMediaTaskIds] = useState<string[]>(
    [],
  );
  const [deletingTaskIds, setDeletingTaskIds] = useState<string[]>([]);
  const [savingTaskIds, setSavingTaskIds] = useState<string[]>([]);

  useEffect(() => {
    const { id, checklistId, instruction } = params;
    setchecklistId(checklistId || instruction);
  }, [params]);

  const initialValues = {
    id: showEdit,
    name: task,
    description: description,
    type: selectType,
    checklist_id: activeChecklistId,
    is_media_upload_required: isMediaUploadRequired,
  };

  const fetchTask = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (showEdit) queryParams.append("id", showEdit.toString());
      const res = await getTask(queryParams);
      if (res.success === true) {
        setTask(res?.data?.name);
        setDescription(res?.data?.description);
        setselectType(res?.data?.type);
        setIsMediaUploadRequired(res?.data?.is_media_upload_required || false);
      }
    } catch (e) {}
  };

  const fetchAllTask = async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoader(true);
      }
      const queryParams = new URLSearchParams();
      queryParams.append("status", "active");
      if (activeChecklistId)
        queryParams.append("checklist_id", activeChecklistId.toString());
      const res = await getTask(queryParams);
      if (res.success === true) {
        const sortedTasks = [...res?.data].sort(
          (a, b) => a.position - b.position,
        );
        setTasks(sortedTasks);
        setLoader(false);
        props?.getTasks(res?.data);
      } else {
        setLoader(false);
      }
    } catch (e) {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (activeChecklistId) {
      fetchAllTask();
    }
  }, [activeChecklistId]);

  useEffect(() => {
    if (activeChecklistId) {
      fetchAllTask(tasks.length === 0);
    }
  }, [props?.taskadded]);

  const { errors, handleSubmit } = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: tasklistSchema,
    onSubmit: async (values) => {
      const previousTasks = tasks;
      const updatedTasks = tasks.map((item) =>
        item.id === values.id
          ? {
              ...item,
              description: values.description,
              type: values.type,
              is_media_upload_required: values.is_media_upload_required,
            }
          : item,
      );

      setSavingTaskIds((ids) => [...ids, values.id]);

      try {
        const res = await updateTasklist(values);
        if (res?.success === true) {
          setTasks(updatedTasks);
          props?.getTasks(updatedTasks);
          setshowEdit("");
        } else {
          setTasks(previousTasks);
        }
      } catch (e) {
        setTasks(previousTasks);
      } finally {
        setSavingTaskIds((ids) => ids.filter((id) => id !== values.id));
      }
    },
  });

  const HandleEdit = (id) => {
    if (isReadOnly) return;

    if (id) {
      setshowEdit(id);
    } else {
      setshowEdit(null);
    }
  };

  useEffect(() => {
    if (showEdit) {
      fetchTask();
    }
  }, [showEdit]);

  const intl = useIntl();

  const HandleDeleteTask = async (id) => {
    if (isReadOnly) return;

    const previousTasks = tasks;
    const filteredTasks = tasks.filter((task) => task.id !== id);
    const updatedTasks = filteredTasks.map((task, index) => ({
      ...task,
      position: index + 1,
    }));

    setDeletingTaskIds((ids) => [...ids, id]);

    try {
      const formData = {
        id,
        status: "draft",
      };
      const res = await updateTasklist(formData);
      if (res?.success === true) {
        setTasks(updatedTasks);
        props?.getTasks(updatedTasks);

        try {
          await reorderTasklist(updatedTasks.map((t) => ({ id: t.id })));
        } catch (e) {
          console.log(e);
        }
      } else {
        setTasks(previousTasks);
      }
    } catch (e) {
      console.log(e);
      setTasks(previousTasks);
    } finally {
      setDeletingTaskIds((ids) => ids.filter((taskId) => taskId !== id));
    }
  };

  const handleMediaRequirementToggle = async (
    taskId: string,
    isRequired: boolean,
  ) => {
    if (isReadOnly) return;

    const previousTask = tasks.find((item) => item.id === taskId);

    setUpdatingMediaTaskIds((ids) => [...ids, taskId]);
    setTasks((currentTasks) =>
      currentTasks.map((item) =>
        item.id === taskId
          ? { ...item, is_media_upload_required: isRequired }
          : item,
      ),
    );

    try {
      const res = await updateTasklist({
        id: taskId,
        is_media_upload_required: isRequired,
      });

      if (res?.success !== true) {
        setTasks((currentTasks) =>
          currentTasks.map((item) =>
            item.id === taskId && previousTask ? previousTask : item,
          ),
        );
      }
    } catch (e) {
      console.log(e);
      setTasks((currentTasks) =>
        currentTasks.map((item) =>
          item.id === taskId && previousTask ? previousTask : item,
        ),
      );
    } finally {
      setUpdatingMediaTaskIds((ids) => ids.filter((id) => id !== taskId));
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (isReadOnly) return;
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reorderedTasks = [...tasks];
    const [removed] = reorderedTasks.splice(result.source.index, 1);
    reorderedTasks.splice(result.destination.index, 0, removed);

    // Optimistic update
    setTasks(reorderedTasks);

    // Call reorder API with ordered IDs
    try {
      await reorderTasklist(reorderedTasks.map((t) => ({ id: t.id })));
    } catch (e) {
      console.log(e);
      // Rollback on failure
      fetchAllTask();
    }
  };

  return (
    <ContentWithSpinner loader={loader}>
      {tasks?.length > 0 ? (
        <DragDropContextComponent onDragEnd={handleDragEnd}>
          <table className="w-full text-left condidate-table">
            <thead className="bg-[#FAF9F8] border-collapse border-b border-neutral-200 capitalize">
              <tr>
                {!isReadOnly && (
                  <th scope="col" className="px-6 py-4 text-base w-10"></th>
                )}
                <th scope="col" className="px-6 py-4 text-base">
                  <FormattedMessage
                    id="RECRUITER.CUSTOMER.CHECKLIST.TASK.TABLE.COL.TASK"
                    defaultMessage="Task"
                  />
                </th>
                <th scope="col" className="px-6 py-4 text-base">
                  <FormattedMessage
                    id="RECRUITER.CUSTOMER.CHECKLIST.TASK.TABLE.COL.DESCRIPTION"
                    defaultMessage="Description"
                  />
                </th>
                <th scope="col" className="px-6 py-4 text-base">
                  <FormattedMessage
                    id="RECRUITER.CUSTOMER.CHECKLIST.TASK.TABLE.COL.TYPE"
                    defaultMessage="Type"
                  />
                </th>
                <th scope="col" className="px-6 py-4 text-base">
                  <div className="flex items-center gap-2">
                    <FormattedMessage
                      id="RECRUITER.CUSTOMER.CHECKLIST.TASK.TABLE.COL.MANDATORY.MEDIA"
                      defaultMessage="Mandatory Media"
                    />
                    <div className="relative group">
                      <span className="inline-flex items-center justify-center w-4 h-4 text-xs text-white bg-gray-400 rounded-full cursor-help hover:bg-gray-500">
                        ?
                      </span>
                      <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-sm rounded-lg px-3 py-2 w-64 -left-28 top-6 shadow-lg">
                        Selecting Mandatory Media will require cleaners to attach a photo or a video to the associated task. Cleaners will not be able to complete the Checklist without attaching media.
                        <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -top-1 left-1/2"></div>
                      </div>
                    </div>
                  </div>
                </th>
                {!isReadOnly && (
                  <th scope="col" className="px-6 py-4 text-base">
                    <FormattedMessage
                      id="RECRUITER.CUSTOMER.CHECKLIST.TASK.TABLE.COL.ACTION"
                      defaultMessage="Action"
                    />
                  </th>
                )}
              </tr>
            </thead>
            <DroppableComponent droppableId="tasks">
              {(provided: DroppableProvided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {tasks?.map((data, index) => (
                    <DraggableComponent
                      key={data.id}
                      draggableId={data.id}
                      index={index}
                      isDragDisabled={isReadOnly}
                    >
                      {(
                        provided: DraggableProvided,
                        snapshot: DraggableStateSnapshot,
                      ) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`bg-white border-b hover:bg-gray-50 ${
                            snapshot.isDragging ? "shadow-lg opacity-80" : ""
                          } ${
                            updatingMediaTaskIds.includes(data.id)
                              ? "opacity-70"
                              : ""
                          } ${
                            deletingTaskIds.includes(data.id)
                              ? "opacity-50 pointer-events-none"
                              : ""
                          } ${
                            savingTaskIds.includes(data.id)
                              ? "opacity-70 pointer-events-none"
                              : ""
                          }`}
                        >
                          {!isReadOnly && (
                            <td
                              className="px-3 py-4 cursor-grab active:cursor-grabbing text-gray-400"
                              {...provided.dragHandleProps}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                                />
                              </svg>
                            </td>
                          )}

                          <th
                            scope="row"
                            className="px-6 py-4 font-medium text-gray-900"
                          >
                            <div className="text-base">{data.name}</div>
                          </th>

                          <td className="px-6 py-4">
                            {showEdit === data?.id && !isReadOnly ? (
                              <textarea
                                onChange={(e) => setDescription(e.target.value)}
                                id="Description"
                                value={description}
                                rows={2}
                                className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:border-[#0078ae] focus:shadow-inputshadow outline-0"
                                placeholder={intl.formatMessage({
                                  id: "RECRUITER.CUSTOMER.INPUT.TASK.DESCRIPTION.PLACEHOLDER",
                                  defaultMessage:
                                    "Write your Description here...",
                                })}
                              />
                            ) : (
                              data.description
                            )}
                          </td>

                          <td className="px-6 py-4">
                            {showEdit === data?.id && !isReadOnly ? (
                              <DropDown
                                dropdowndiv="text-gray-700 mt-0"
                                labelMargin="mb-1"
                                value={selectType}
                                onChange={(e) => setselectType(e.target.value)}
                                className="border border-zinc-800 rounded-lg h-11"
                              >
                                <option value="">
                                  <FormattedMessage
                                    id="RECRUITER.CUSTOMER.CHECKLIST.TASK.INPUT.SELECT.TYPE"
                                    defaultMessage="Select Type"
                                  />
                                </option>
                                <option value="Mandatory">Mandatory</option>
                                <option value="Optional">Optional</option>
                              </DropDown>
                            ) : (
                              data.type
                            )}
                          </td>

                          <td className="px-6 py-4">
                            {showEdit === data?.id && !isReadOnly ? (
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isMediaUploadRequired}
                                  onChange={(e) => setIsMediaUploadRequired(e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            ) : isReadOnly ? (
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                                  data?.is_media_upload_required
                                    ? "bg-green-50 text-green-700"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {data?.is_media_upload_required ? "Yes" : "No"}
                              </span>
                            ) : (
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={data?.is_media_upload_required || false}
                                  disabled={updatingMediaTaskIds.includes(data.id)}
                                  onChange={(e) =>
                                    handleMediaRequirementToggle(
                                      data.id,
                                      e.target.checked,
                                    )
                                  }
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            )}
                          </td>

                          {!isReadOnly && (
                            <td className="px-6 py-4">
                              {showEdit === data?.id ? (
                                <div className="flex justify-center w-auto">
                                  <Button
                                    onClick={() =>
                                      !savingTaskIds.includes(data.id) &&
                                      handleSubmit()
                                    }
                                    justify="justify-center"
                                    text={
                                      <FormattedMessage
                                        id="RECRUITER.CUSTOMER.CHECKLIST.TASK.SAVE_BUTTON"
                                        defaultMessage="Save"
                                      />
                                    }
                                  />
                                  <Button
                                    onClick={() => HandleEdit("")}
                                    justify="justify-center"
                                    text={
                                      <FormattedMessage
                                        id="RECRUITER.CUSTOMER.CHECKLIST.TASK.CANCEL_BUTTON"
                                        defaultMessage="Cancel"
                                      />
                                    }
                                  />
                                </div>
                              ) : (
                                <div className="flex items-center space-x-4">
                                  <div className="border border-neutral-200 rounded-lg inline-flex divide-x w-auto">
                                    <div
                                      onClick={() => HandleEdit(data?.id)}
                                      className="py-1.5 px-2 group cursor-pointer relative hover:text-[#0291d1] hover:rounded-l-lg hover:border-2 hover:border-[#0291d1] hover:bg-white"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="w-8 h-6 mt-1"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125"
                                        />
                                      </svg>
                                      <span className="absolute top-10 scale-0 rounded bg-gray-800 p-2 text-xs text-white group-hover:scale-100">
                                        <FormattedMessage
                                          id="ECOMMERCE.COMMON.ICON.TOOLTIP.EDIT"
                                          defaultMessage="Edit"
                                        />
                                      </span>
                                    </div>
                                    <div
                                      onClick={() =>
                                        !deletingTaskIds.includes(data.id) &&
                                        HandleDeleteTask(data?.id)
                                      }
                                      className={`py-1.5 px-2 group relative hover:text-[#a9252b] hover:border-2 hover:rounded-r-lg hover:border-[#f76266] hover:bg-[#FEEEEF] ${
                                        deletingTaskIds.includes(data.id)
                                          ? "cursor-not-allowed"
                                          : "cursor-pointer"
                                      }`}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="w-8 h-8 text-black"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M6 18L18 6M6 6l12 12"
                                        />
                                      </svg>
                                      <span className="absolute top-10 scale-0 rounded bg-gray-800 p-2 text-xs text-white group-hover:scale-100">
                                        <FormattedMessage
                                          id="ECOMMERCE.COMMON.ICON.TOOLTIP.DELETE"
                                          defaultMessage="Delete"
                                        />
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      )}
                    </DraggableComponent>
                  ))}
                  {provided.placeholder}
                </tbody>
              )}
            </DroppableComponent>
          </table>
        </DragDropContextComponent>
      ) : (
        <p className="text-center">You haven't added any tasks</p>
      )}
    </ContentWithSpinner>
  );
};
